import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-hot-toast";
import adminService from "../../services/adminService";
import HotelReservationAdminModal from "./HotelReservationAdminModal";
import TransportReservationAdminModal from "./TransportReservationAdminModal";
import VisiteReservationAdminModal from "./VisiteReservationAdminModal";
import VoitureReservationAdminModal from "./VoitureReservationAdminModal";

/* ---------- Helpers ---------- */
const money = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : new Intl.NumberFormat("fr-MA", { style: "currency", currency: "MAD", minimumFractionDigits: 2 }).format(Number(n));

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";

const pick = (obj, paths, fb = "") => {
  for (const p of paths) {
    const v = p.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fb;
};

// Date de création de la réservation (pour le tri « plus récentes »).
const createdOf = (r) => pick(r, ["createdAt", "created_at", "dateCreation", "date_creation", "updatedAt"], null);

// Temps relatif lisible : « à l'instant », « il y a 2 h », « hier », « il y a 3 j », sinon date.
const relativeTime = (d) => {
  if (!d) return "";
  const then = new Date(d).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j === 1) return "hier";
  if (j < 7) return `il y a ${j} j`;
  return formatDate(d);
};

const statutOf = (r) => String(pick(r, ["statut", "status"], "")).toLowerCase();
const isPaid = (r) => statutOf(r).includes("pay");
const isCancelled = (r) => statutOf(r).includes("annul") || statutOf(r).includes("cancel");

// Badge de statut générique (couleur par mot-clé)
const StatutBadge = ({ statut }) => {
  const s = String(statut || "").toLowerCase();
  let cls = "bg-slate-100 text-slate-600";
  const label = statut || "—";
  if (s.includes("attente") || s.includes("pending")) cls = "bg-amber-100 text-amber-700";
  else if (s.includes("affect") || s.includes("assign")) cls = "bg-blue-100 text-blue-700";
  else if (s.includes("devis")) cls = "bg-indigo-100 text-indigo-700";
  else if (s.includes("confirm")) cls = "bg-teal-100 text-teal-700";
  else if (s.includes("pay")) cls = "bg-emerald-100 text-emerald-700";
  else if (s.includes("annul") || s.includes("cancel")) cls = "bg-red-100 text-red-700";
  return <span className={`px-2.5 py-1 inline-flex text-[11px] leading-4 font-bold rounded-full ${cls}`}>{label}</span>;
};

// Liseré coloré à gauche de la carte, selon le statut.
const accentOf = (r) => {
  const s = statutOf(r);
  if (s.includes("pay")) return "border-l-emerald-400";
  if (s.includes("annul") || s.includes("cancel")) return "border-l-red-300";
  if (s.includes("confirm")) return "border-l-teal-400";
  if (s.includes("devis")) return "border-l-indigo-400";
  if (s.includes("affect")) return "border-l-blue-400";
  if (s.includes("attente") || s.includes("pending")) return "border-l-amber-400";
  return "border-l-slate-200";
};

const UNASSIGNED = "__unassigned__";

/* ---------- Config par service ---------- */
const CONFIG = {
  reservationHotels: {
    title: "Réservations Hôtels",
    emoji: "🏨",
    noun: "hôtel",
    Modal: HotelReservationAdminModal,
    getService: (r) => r.hotel,
    serviceTitle: (svc) => svc?.nom,
    serviceImage: (svc) => svc?.image,
    pricePaths: ["prixFinal", "prixTotal", "prix_total", "cmi.montantPaye"],
  },
  reservationTransports: {
    title: "Réservations Transports",
    emoji: "🚌",
    noun: "transport",
    Modal: TransportReservationAdminModal,
    getService: (r) => r.transport,
    serviceTitle: (svc) => svc?.nom || svc?.titre || svc?.type,
    serviceImage: (svc) => svc?.image,
    pricePaths: ["prixFinal", "prix_total", "prixTotal", "paiement.montantPaye"],
  },
  reservationVisites: {
    title: "Réservations Visites Guidées",
    emoji: "👁️",
    noun: "visite",
    Modal: VisiteReservationAdminModal,
    getService: (r) => r.visite,
    serviceTitle: (svc) => svc?.titre || svc?.nom,
    serviceImage: (svc) => svc?.image,
    pricePaths: ["prixFinal", "prix_total", "prixTotal", "paiement.montantPaye"],
  },
  reservationVoitures: {
    title: "Réservations Voitures",
    emoji: "🚗",
    noun: "voiture",
    Modal: VoitureReservationAdminModal,
    getService: (r) => r.voiture,
    serviceTitle: (svc) => svc?.nom || `${svc?.marque || ""} ${svc?.modele || ""}`.trim() || svc?.titre,
    serviceImage: (svc) => svc?.image,
    pricePaths: ["prixFinal", "prix_total", "prixTotal", "paiement.montantPaye", "budgetParJour"],
  },
};

/* ---------- Petites briques UI ---------- */
const StatCard = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "from-slate-700 to-slate-900 text-white/60",
    emerald: "from-emerald-500 to-emerald-700 text-white/70",
    amber: "from-amber-500 to-orange-600 text-white/70",
    indigo: "from-indigo-500 to-indigo-700 text-white/70",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${tones[tone]} text-white px-4 py-2.5 min-w-[110px]`}>
      <p className="text-[10px] uppercase tracking-wide font-bold opacity-90">{label}</p>
      <p className="text-lg font-black leading-tight">{value}</p>
    </div>
  );
};

/* ---------- Composant ---------- */
const ReservationsByServiceManager = ({ modelName }) => {
  const cfg = CONFIG[modelName];
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous");
  const [sortBy, setSortBy] = useState("recent"); // recent | old | amount
  const [viewMode, setViewMode] = useState("grouped"); // grouped | list
  const [selected, setSelected] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const fetchReservations = useCallback(async () => {
    if (!cfg) return;
    setLoading(true);
    try {
      const res = await adminService.getAllItems(modelName, { limit: 300 });
      const items = res?.data?.items || res?.data || [];
      setReservations(Array.isArray(items) ? items : []);
    } catch (e) {
      toast.error(e.message || "Erreur de chargement des réservations");
    } finally {
      setLoading(false);
    }
  }, [cfg, modelName]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const price = useCallback((r) => pick(r, cfg?.pricePaths || [], null), [cfg]);

  // Tri d'une liste de réservations selon le critère choisi.
  const sortItems = useCallback(
    (arr) => {
      const byRecent = (a, b) => new Date(createdOf(b) || 0) - new Date(createdOf(a) || 0);
      const byOld = (a, b) => new Date(createdOf(a) || 0) - new Date(createdOf(b) || 0);
      const byAmount = (a, b) => (Number(price(b)) || 0) - (Number(price(a)) || 0);
      const cmp = sortBy === "old" ? byOld : sortBy === "amount" ? byAmount : byRecent;
      return [...arr].sort(cmp);
    },
    [sortBy, price]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reservations.filter((r) => {
      const statut = statutOf(r);
      if (statusFilter !== "tous" && !statut.includes(statusFilter)) return false;
      if (!q) return true;
      const svc = cfg?.getService(r);
      return [
        pick(r, ["fullName", "client.fullName", "nom"]),
        pick(r, ["email", "client.email"]),
        pick(r, ["telephone", "phone", "client.telephone"]),
        pick(r, ["reference"]),
        cfg?.serviceTitle(svc),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [reservations, search, statusFilter, cfg]);

  // Vue « liste » : à plat, triée globalement.
  const flatSorted = useMemo(() => sortItems(filtered), [filtered, sortItems]);

  // Vue « groupée » : par service, items triés, groupes ordonnés selon le tri.
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const svc = cfg?.getService(r);
      const key = svc?._id || UNASSIGNED;
      if (!map.has(key)) {
        map.set(key, {
          key,
          title: (svc && cfg?.serviceTitle(svc)) || `Non affecté à un ${cfg?.noun}`,
          image: svc ? cfg?.serviceImage(svc) : null,
          items: [],
          revenue: 0,
          paidCount: 0,
          newest: 0,
        });
      }
      const g = map.get(key);
      g.items.push(r);
      const created = new Date(createdOf(r) || 0).getTime();
      if (created > g.newest) g.newest = created;
      if (isPaid(r)) {
        g.revenue += Number(price(r) || 0);
        g.paidCount += 1;
      }
    });
    const arr = Array.from(map.values()).map((g) => ({ ...g, items: sortItems(g.items) }));
    return arr.sort((a, b) => {
      if (a.key === UNASSIGNED) return 1;
      if (b.key === UNASSIGNED) return -1;
      if (sortBy === "amount") return b.revenue - a.revenue;
      if (sortBy === "old") return a.newest - b.newest;
      return b.newest - a.newest; // recent
    });
  }, [filtered, cfg, price, sortItems, sortBy]);

  // KPIs
  const stats = useMemo(() => {
    let paid = 0,
      pending = 0,
      revenue = 0;
    reservations.forEach((r) => {
      if (isPaid(r)) {
        paid += 1;
        revenue += Number(price(r) || 0);
      } else if (!isCancelled(r)) pending += 1;
    });
    return { total: reservations.length, paid, pending, revenue };
  }, [reservations, price]);

  const toggle = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  if (!cfg) return <div className="p-6 text-slate-500">Service inconnu.</div>;
  const Modal = cfg.Modal;

  /* ---- Carte réservation ---- */
  const ReservationCard = ({ r, showService = false }) => {
    const svc = cfg.getService(r);
    return (
      <div className={`rounded-2xl border border-slate-100 border-l-4 ${accentOf(r)} bg-white p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-bold text-slate-800 truncate">{pick(r, ["fullName", "client.fullName", "nom"], "Client")}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-mono text-slate-400">{pick(r, ["reference"], r._id?.slice(-8))}</span>
              {createdOf(r) && <span className="text-[11px] text-slate-400">· {relativeTime(createdOf(r))}</span>}
            </div>
          </div>
          <StatutBadge statut={pick(r, ["statut", "status"], "")} />
        </div>

        {showService && (
          <span className="mt-2 self-start inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-lg">
            {cfg.emoji} {(svc && cfg.serviceTitle(svc)) || `Non affecté`}
          </span>
        )}

        <div className="mt-2.5 space-y-1 text-xs text-slate-600 flex-1">
          <p className="truncate">✉️ {pick(r, ["email", "client.email"], "—")}</p>
          <p>📞 {pick(r, ["telephone", "phone", "client.telephone"], "—")}</p>
          <p>
            📅 {formatDate(pick(r, ["dateDebut", "dateArrivee", "date", "dateDepart"], ""))} · 👥{" "}
            {pick(r, ["nombrePersonnes", "nb_personnes", "participants", "nombreAdultes"], "?")}
          </p>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-black text-slate-800 text-base">{money(price(r))}</span>
          {isPaid(r) && <span className="text-[11px] font-bold text-emerald-600">✓ Payé</span>}
        </div>

        <button
          onClick={() => setSelected(r)}
          className="mt-3 w-full px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold hover:from-amber-600 hover:to-orange-700 transition-all"
        >
          ⚙️ Gérer &amp; paiement
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              {cfg.emoji} {cfg.title}
            </h2>
            <p className="text-sm text-slate-500">Suivi, tri et paiement des réservations</p>
          </div>
          <div className="flex gap-2.5 flex-wrap">
            <StatCard label="Total" value={stats.total} tone="slate" />
            <StatCard label="À traiter" value={stats.pending} tone="amber" />
            <StatCard label="Payées" value={stats.paid} tone="indigo" />
            <StatCard label="Encaissé" value={money(stats.revenue)} tone="emerald" />
          </div>
        </div>

        {/* Barre d'outils : recherche, tri, statut, vue */}
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <input
            type="text"
            placeholder={`🔎 Rechercher (client, email, réf, ${cfg.noun})…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[220px] px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-amber-400 outline-none text-sm"
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white font-semibold text-slate-700"
            title="Trier"
          >
            <option value="recent">⏱️ Plus récentes</option>
            <option value="old">📜 Plus anciennes</option>
            <option value="amount">💰 Montant décroissant</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white"
          >
            <option value="tous">Tous statuts</option>
            <option value="attente">À traiter</option>
            <option value="affect">Affecté</option>
            <option value="devis">Devis envoyé</option>
            <option value="confirm">Confirmé</option>
            <option value="pay">Payé</option>
            <option value="annul">Annulé</option>
          </select>

          {/* Bascule vue */}
          <div className="inline-flex rounded-xl border-2 border-slate-200 overflow-hidden">
            <button
              onClick={() => setViewMode("grouped")}
              className={`px-3 py-2.5 text-sm font-bold transition-colors ${viewMode === "grouped" ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              title={`Regrouper par ${cfg.noun}`}
            >
              ▦ Par {cfg.noun}
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-2.5 text-sm font-bold transition-colors ${viewMode === "list" ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              title="Liste chronologique"
            >
              ☰ Liste
            </button>
          </div>
        </div>

        {(search || statusFilter !== "tous") && (
          <p className="mt-3 text-xs text-slate-500">
            {filtered.length} résultat(s){search && <> pour « {search} »</>}
          </p>
        )}
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="space-y-4">
          <style>{`.nz-skel{position:relative;overflow:hidden;background:#eef2f7}.nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}@keyframes nz-shimmer{100%{transform:translateX(100%)}}`}</style>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-3xl border border-slate-100 p-5 space-y-3">
              <div className="h-5 w-1/3 rounded nz-skel" />
              <div className="h-16 w-full rounded-2xl nz-skel" />
              <div className="h-16 w-full rounded-2xl nz-skel" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 text-center py-16">
          <div className="text-5xl mb-3">📭</div>
          <p className="text-slate-500">Aucune réservation{search || statusFilter !== "tous" ? " pour ce filtre" : ""}.</p>
        </div>
      ) : viewMode === "list" ? (
        /* -------- Vue LISTE (chronologique) -------- */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {flatSorted.map((r) => (
            <ReservationCard key={r._id} r={r} showService />
          ))}
        </div>
      ) : (
        /* -------- Vue GROUPÉE -------- */
        <div className="space-y-4">
          {groups.map((g) => {
            const isCollapsed = collapsed[g.key];
            return (
              <div key={g.key} className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <button
                  onClick={() => toggle(g.key)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="w-14 h-14 flex-none rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center">
                    {g.image ? (
                      <img src={g.image} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                    ) : (
                      <span className="text-2xl">{g.key === UNASSIGNED ? "❓" : cfg.emoji}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-black text-slate-800 truncate">{g.title}</p>
                    <p className="text-xs text-slate-400">
                      {g.items.length} réservation(s) · {g.paidCount} payée(s)
                      {g.newest > 0 && <> · dernière {relativeTime(g.newest)}</>}
                    </p>
                  </div>
                  {g.revenue > 0 && (
                    <span className="hidden sm:inline-flex text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-xl">{money(g.revenue)}</span>
                  )}
                  <span className={`text-slate-400 transition-transform ${isCollapsed ? "" : "rotate-180"}`}>▾</span>
                </button>

                {!isCollapsed && (
                  <div className="px-4 lg:px-5 pb-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {g.items.map((r) => (
                      <ReservationCard key={r._id} r={r} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selected && <Modal reservation={selected} onClose={() => setSelected(null)} onUpdated={() => fetchReservations()} />}
    </div>
  );
};

export default ReservationsByServiceManager;
